import {
  assuranceLabel,
  impactLabel,
  statusLabel,
  type AnalysisBundle,
  type ExitLadderRow,
} from "./shared/report";

export function reportToMarkdown(report: AnalysisBundle): string {
  const identityName = [report.identity.name, report.identity.symbol && `(${report.identity.symbol})`]
    .filter(Boolean)
    .join(" ");
  const identitySourceLines = report.identityReport.sources.length
    ? report.identityReport.sources.map(
        (source) => `- Registry source (${source.recognition.replaceAll("_", " ")}): ${source.label}${source.url ? ` — ${source.url}` : ""}`,
      )
    : [
        report.identityReport.status === "unknown"
          ? "- Registry sources: One or more checks did not complete"
          : "- Registry sources: No positive exact-mint source",
      ];
  const lines = [
    "# Rug Lens report",
    "",
    report.mode === "fixture"
      ? `> MADE-UP DEMO — ${report.fixtureLabel ?? "made-up scenario"}. This is not live token data.`
      : "> Live observation at the time shown. This is not financial advice or a guarantee of future execution.",
    "",
    `- Token: ${identityName || "Untrusted metadata unavailable"}`,
    `- Chain: ${report.identity.network}`,
    `- Mint: ${report.identity.address}`,
    `- Token program: ${report.identity.tokenProgram}`,
    `- Asset type: ${report.identity.assetType.replaceAll("_", " ")}`,
    `- Identity status: ${report.identityReport.label}`,
    `- Generated: ${report.generatedAt}`,
    `- Methodology: ${report.identityReport.methodologyVersion}`,
    "",
    "## Identity / Registry Check",
    "",
    report.identityReport.headline,
    "",
    ...report.identityReport.plainSummary,
    "",
    `- Complete-address result: ${report.identityReport.label}`,
    `- Recognized mint: ${report.identityReport.recognizedAddress ?? "Not established"}`,
    `- Matched using: ${report.identityReport.matchedFields.join(", ")}`,
    ...identitySourceLines,
    "",
    "## Rug Capacity",
    "",
    report.rugCapacity.headline,
    "",
  ];

  for (const finding of report.rugCapacity.findings) {
    lines.push(
      `### ${finding.title} — ${statusLabel(finding.status)}`,
      "",
      finding.summary,
      "",
      `- Impact class: ${impactLabel(finding.impactClass)}`,
      `- Estimated exposure: ${finding.estimatedExposure}`,
      `- Controller: ${finding.control.controller ?? "Not identified"}`,
      `- Authority type: ${finding.control.kind.replaceAll("_", " ")}`,
      `- Controlling program: ${finding.control.controllingProgram ?? "Not established"}`,
      `- Control constraints: ${finding.control.minimumCoalition}; ${finding.control.warningDelay}`,
      `- Coverage: ${finding.coverage}`,
      "",
    );
    if (finding.liquidityControl) {
      const liquidity = finding.liquidityControl;
      lines.push(
        `- Pool scope: ${liquidity.poolType ?? "No eligible pool selected"}${liquidity.poolAddress ? ` — ${liquidity.poolAddress}` : ""}`,
        `- Visible pool liquidity: ${liquidity.liquidityUsd === null ? "Unavailable" : `$${liquidity.liquidityUsd}`}`,
        `- LP mint: ${liquidity.lpMint ?? "Not decoded"}`,
        `- Top accounts / current live LP supply: ${liquidity.topAccountsCoveragePercentage === null ? "Not calculated" : `${liquidity.topAccountsCoveragePercentage}%`}`,
        `- Provably burned withdrawal claim: ${liquidity.burnedLpSharePercentage === null ? "Not calculated" : `${liquidity.burnedLpSharePercentage}%`}`,
        `- Effective withdrawal-claim coverage: ${liquidity.effectiveClaimCoveragePercentage === null ? "Not calculated" : `${liquidity.effectiveClaimCoveragePercentage}%`}`,
        `- Opaque live withdrawal claim: ${liquidity.opaqueLiveClaimPercentage === null ? "Not calculated" : `${liquidity.opaqueLiveClaimPercentage}%`}`,
        `- Largest direct controller: ${liquidity.largestDirectControllerSharePercentage === null ? "Not calculated" : `${liquidity.largestDirectControllerSharePercentage}%`}`,
        `- Current withdrawal flag: ${liquidity.withdrawalsCurrentlyEnabled === null ? "Not decoded" : liquidity.withdrawalsCurrentlyEnabled ? "Enabled" : "Disabled"}`,
        `- Creator relationship: Not checked${liquidity.poolCreatorAddress ? ` (pool creator address: ${liquidity.poolCreatorAddress})` : ""}`,
        ...liquidity.coalitionThresholds.map(
          (threshold) =>
            `- ${threshold.targetPercentage}% direct-address coalition: ${threshold.minimumControllerAddresses === null ? "Not reached in the top set" : `${threshold.minimumControllerAddresses} address${threshold.minimumControllerAddresses === 1 ? "" : "es"}`}`,
        ),
        "",
      );
      if (liquidity.controllers.length) {
        lines.push(
          "| Rank | LP owner authority | Classification | Pool withdrawal claim |",
          "| ---: | --- | --- | ---: |",
          ...liquidity.controllers.map(
            (controller) =>
              `| ${controller.rank} | ${controller.authority ?? "Unresolved"} | ${controller.authorityKind.replaceAll("_", " ")} | ${controller.shareOfWithdrawalBasePercentage}% |`,
          ),
          "",
        );
      }
    }
  }

  lines.push(
    "## Dump Capacity",
    "",
    report.dumpCapacity.headline,
    "",
    ...report.dumpCapacity.plainSummary,
    "",
    `- Risk band: ${report.dumpCapacity.riskBand.replaceAll("_", " ")}`,
    `- Largest wallet-shaped holder: ${report.dumpCapacity.largestWalletShapedSharePercentage === null ? "Unknown" : `${report.dumpCapacity.largestWalletShapedSharePercentage}%`}`,
    `- Wallet-shaped share in top set: ${report.dumpCapacity.walletShapedSharePercentage}%`,
    `- Program-controlled share in top set: ${report.dumpCapacity.programControlledSharePercentage}%`,
    `- Unresolved share in top set: ${report.dumpCapacity.unresolvedSharePercentage}%`,
    "",
    "| Rank | Owner authority | Classification | Supply share | Token amount |",
    "| ---: | --- | --- | ---: | ---: |",
  );
  if (report.dumpCapacity.holders.length) {
    for (const holder of report.dumpCapacity.holders) {
      lines.push(
        `| ${holder.rank} | ${holder.authority ?? "Unresolved"} | ${holder.authorityKind.replaceAll("_", " ")} | ${holder.shareOfSupplyPercentage}% | ${holder.tokenAmount} |`,
      );
    }
  } else {
    lines.push("| — | No holder rows available | unresolved | — | — |");
  }

  lines.push(
    "",
    "## Exit Liquidity",
    "",
    report.executableExit.headline,
    "",
    `Reference: ${report.executableExit.reference.status === "available" ? `$${report.executableExit.reference.priceUsd} via ${report.executableExit.reference.source}` : "Unavailable"}`,
    "",
    "| Test size | Token input | Quoted output | Recovery | Assurance | Result |",
    "| ---: | ---: | ---: | ---: | --- | --- |",
  );
  for (const row of report.executableExit.rows) {
    lines.push(exitRowMarkdown(row));
  }

  lines.push(
    "",
    `Simulation note: ${report.executableExit.simulationNote}`,
    "",
    "### Limitations",
    "",
    ...[
      ...report.identityReport.limitations,
      ...report.rugCapacity.limitations,
      ...report.dumpCapacity.limitations,
      ...report.executableExit.limitations,
    ].map(
      (item) => `- ${item}`,
    ),
    "",
  );
  return lines.join("\n");
}

export function downloadReport(report: AnalysisBundle, format: "json" | "markdown"): void {
  const stamp = report.generatedAt.slice(0, 19).replace(/[:T]/g, "-");
  const base = `rug-lens-${report.mode}-${stamp}`;
  const content =
    format === "json" ? JSON.stringify(report, null, 2) : reportToMarkdown(report);
  const blob = new Blob([content], {
    type: format === "json" ? "application/json" : "text/markdown",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${base}.${format === "json" ? "json" : "md"}`;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function exitRowMarkdown(row: ExitLadderRow): string {
  const size = row.requestedUsd === null ? `${row.inputTokenAmount} tokens` : usd(row.requestedUsd);
  const output = row.expectedOutputAssetDelta === null ? "Unavailable" : `${usd(row.expectedOutputAssetDelta)} USDC`;
  const recovery =
    row.estimatedRecoveryPercentage === null
      ? "Unavailable"
      : `${row.estimatedRecoveryPercentage.toFixed(1)}%`;
  return `| ${size} | ${row.inputTokenAmount} | ${output} | ${recovery} | ${assuranceLabel(row.assurance)} | ${row.state.replaceAll("_", " ")} |`;
}

function usd(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(value);
}
